import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { request } from '../../config/helpers/axios-instance';

const WHATSAPP_ENDPOINT = 'userDetail/admin/flipWhatsAppRemindersActiveStatus/';
const APP_NOTIFICATIONS_ENDPOINT = 'userDetail/admin/flipAppNotificationsActiveStatus/';
const NOTIFICATION_STATUS_ENDPOINT = 'userDetail/admin/notificationStatus/';

const getResponseData = (response) => response?.data?.data || response?.data || {};

const isRequestFailed = (response) => response?.response?.status >= 400 || response?.status >= 400 || response?.data?.success === false;

const ReminderSwitch = ({ label, checked, disabled, loading, onToggle }) => (
    <label className={`relative inline-flex gap-3 items-center ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
        <input
            type="checkbox"
            className="sr-only peer border-none outline-none"
            checked={checked}
            disabled={disabled}
            onChange={onToggle}
        />
        <div className="relative flex items-center justify-start peer-checked:justify-end p-[2px] w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:bg-[#00A48B]">
            <div className="absolute bg-white border border-gray-300 rounded-full h-5 w-5 transition-all"></div>
        </div>
        <span className="flex flex-col font-sans">
            <span className="font-medium text-gray-800">{label}</span>
            <span className="text-sm text-gray-500">{checked ? 'Enabled' : 'Disabled'}{loading ? ' - Updating...' : ''}</span>
        </span>
    </label>
);

ReminderSwitch.propTypes = {
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const ReminderSwitches = ({
    userId,
    initialWhatsAppRemindersActive = false,
    initialAppNotificationsActive = false,
    apiBasePath = process.env.REACT_APP_BACKEND_URL,
    authToken,
    onUpdateSuccess,
}) => {
    const [whatsAppRemindersActive, setWhatsAppRemindersActive] = useState(Boolean(initialWhatsAppRemindersActive));
    const [appNotificationsActive, setAppNotificationsActive] = useState(Boolean(initialAppNotificationsActive));
    const [whatsAppLoading, setWhatsAppLoading] = useState(false);
    const [appNotificationsLoading, setAppNotificationsLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusAvailable, setStatusAvailable] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const fetchNotificationStatus = useCallback(async () => {
        if (!userId) {
            setStatusAvailable(false);
            setStatusLoading(false);
            return;
        }
        setStatusLoading(true);
        setErrorMessage('');

        try {
            const response = await request({
                url: `${NOTIFICATION_STATUS_ENDPOINT}${userId}`,
                method: 'GET',
            })

            if (isRequestFailed(response)) {
                throw new Error('Notification status fetch failed');
            }

            const status = getResponseData(response);
            setWhatsAppRemindersActive(Boolean(status.whatsAppReminders));
            setAppNotificationsActive(Boolean(status.appNotifications));
            setStatusAvailable(true);
        } catch (error) {
            setStatusAvailable(false);
            setErrorMessage('Could not load reminder status. Please try again.');
        } finally {
            setStatusLoading(false);
        }
    }, [apiBasePath, authToken, userId]);

    useEffect(() => {
        fetchNotificationStatus();
    }, [fetchNotificationStatus]);

    const flipReminderStatus = async (endpoint, previousValue, setValue, setLoading) => {
        setErrorMessage('');
        setValue(!previousValue);
        setLoading(true);

        try {
            const response = await request({
                url: `${endpoint}${userId}`,
                method: 'PUT',
            });

            if (isRequestFailed(response)) {
                throw new Error('Reminder update failed');
            }

            await fetchNotificationStatus();

            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
        } catch (error) {
            setValue(previousValue);
            setErrorMessage('Could not update reminder setting. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const switchesDisabled = statusLoading || !statusAvailable;

    return (
        <div className="mt-6 p-4 bg-white rounded-2xl shadow-soft-xl">
            <h2 className="text-[#168c7c] font-bold text-lg font-sans mb-4">Reminder settings</h2>
            {statusLoading ? <p className="mb-3 text-sm text-gray-500">Loading reminder status...</p> : null}
            <div className="flex flex-row gap-8 flex-wrap">
                <ReminderSwitch
                    label="WhatsApp reminders"
                    checked={whatsAppRemindersActive}
                    loading={whatsAppLoading}
                    disabled={switchesDisabled || whatsAppLoading}
                    onToggle={() => {
                        flipReminderStatus(WHATSAPP_ENDPOINT, whatsAppRemindersActive, setWhatsAppRemindersActive, setWhatsAppLoading);
                    }}
                />
                <ReminderSwitch
                    label="App notification reminders"
                    checked={appNotificationsActive}
                    loading={appNotificationsLoading}
                    disabled={switchesDisabled || appNotificationsLoading}
                    onToggle={() => {
                        flipReminderStatus(APP_NOTIFICATIONS_ENDPOINT, appNotificationsActive, setAppNotificationsActive, setAppNotificationsLoading);
                    }}
                />
            </div>
            {errorMessage ? <p className="mt-3 text-sm text-red-600">{errorMessage}</p> : null}
        </div>
    );
};

ReminderSwitches.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    initialWhatsAppRemindersActive: PropTypes.bool,
    initialAppNotificationsActive: PropTypes.bool,
    apiBasePath: PropTypes.string,
    authToken: PropTypes.string,
    onUpdateSuccess: PropTypes.func,
};

export default ReminderSwitches;
